import { ChevronRight, KeyRound, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { Toggle } from '@/components/ui/Toggle'
import { toNumber } from '@/lib/decimal'
import { formatContractHours, formatEuros } from '@/lib/format'
import {
  useDeleteContractHours,
  usePersonOAuthClients,
  usePersonRates,
  usePersons,
  useReactivatePersonOAuthClient,
  useRevokePersonOAuthClient,
  useSavePerson,
  useUpdateAccess,
} from '@/lib/settings-api'
import type { PersonRateRow, PersonRow } from '@/lib/settings-types'
import type { UserRole } from '@/lib/types'
import { useClientList } from '@/lib/clients-api'
import { cn } from '@/lib/utils'
import {
  AccountEmailModal,
  AccountModal,
  ContractHoursModal,
  PersonModal,
  RateModal,
} from './person-modals'
import { ROLE_LABEL, ROLE_SELECT_OPTIONS, formatDate } from './shared'
import { TemplatesCard } from './templates'

export function AdminTab() {
  return (
    <div className="space-y-6">
      <PeopleCard />
      <TemplatesCard canEdit />
    </div>
  )
}

function accountStatus(person: PersonRow): { label: string; dot: string } {
  if (!person.active) return { label: 'Gedeactiveerd', dot: 'bg-status-overdue' }
  if (!person.account?.exists) return { label: 'Geen account', dot: 'bg-fill-3' }
  return { label: 'Account', dot: 'bg-accent-600' }
}

function PeopleCard() {
  const personsQuery = usePersons()
  const ratesQuery = usePersonRates()
  const persons = personsQuery.data ?? []
  const rates = ratesQuery.data ?? []
  const [creating, setCreating] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  const activePersons = persons.filter((p) => p.active)
  const inactivePersons = persons.filter((p) => !p.active)

  return (
    <Card>
      <CardHeader
        title="Personen"
        subtitle="Account, rol, contracturen, declarabiliteit en uitbetaaltarieven per persoon"
        action={
          <button type="button" className="btn-secondary h-8 px-2.5 text-sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Nieuwe persoon
          </button>
        }
      />
      {personsQuery.isError ? (
        <ErrorState compact error={personsQuery.error} onRetry={() => personsQuery.refetch()} />
      ) : personsQuery.isLoading ? (
        <Skeleton className="mt-4 h-40 w-full" />
      ) : (
        <div className="mt-4">
          <div className="divide-y divide-hairline">
            {activePersons.map((person) => (
              <PersonRowItem
                key={person.id}
                person={person}
                rates={rates.filter((r) => r.personId === person.id)}
                expanded={expanded === person.id}
                onToggle={() => setExpanded(expanded === person.id ? null : person.id)}
              />
            ))}
          </div>
          {inactivePersons.length > 0 ? (
            <div className="mt-3 border-t border-hairline pt-3">
              <button
                type="button"
                onClick={() => setShowInactive((v) => !v)}
                aria-expanded={showInactive}
                className="flex items-center gap-1.5 text-sm text-ink-faint hover:text-ink"
              >
                <ChevronRight className={cn('h-4 w-4 transition-transform', showInactive ? 'rotate-90' : '')} strokeWidth={1.85} />
                Inactief ({inactivePersons.length})
              </button>
              {showInactive ? (
                <div className="mt-2 divide-y divide-hairline">
                  {inactivePersons.map((person) => (
                    <PersonRowItem
                      key={person.id}
                      person={person}
                      rates={rates.filter((r) => r.personId === person.id)}
                      expanded={expanded === person.id}
                      onToggle={() => setExpanded(expanded === person.id ? null : person.id)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {creating ? <PersonModal person={null} onClose={() => setCreating(false)} /> : null}
    </Card>
  )
}

function PersonRowItem({ person, rates, expanded, onToggle }: { person: PersonRow; rates: PersonRateRow[]; expanded: boolean; onToggle: () => void }) {
  const status = accountStatus(person)
  const contract = person.contractHours?.current ?? null

  return (
    <div>
      <button type="button" onClick={onToggle} aria-expanded={expanded} className="flex w-full items-center gap-3 py-3 text-left">
        <ChevronRight className={cn('h-4 w-4 shrink-0 text-ink-faint transition-transform', expanded ? 'rotate-90' : '')} strokeWidth={1.85} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">
            {person.displayName}
            {!person.active ? <span className="ml-2 rounded-pill bg-fill-2 px-2 py-0.5 text-xs text-ink-faint">inactief</span> : null}
          </p>
          <p className="truncate text-xs text-ink-faint">
            {person.roleLabel ?? ROLE_LABEL[person.role]}
            {contract ? ` · ${formatContractHours(toNumber(contract.hoursPerWeek))} u/week` : ''}
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-1.5 rounded-md bg-fill-1 px-2 py-1 text-xs text-ink-soft ring-1 ring-hairline">
          <span className={cn('h-1.5 w-1.5 rounded-full', status.dot)} />
          {status.label}
        </span>
      </button>
      {expanded ? <PersonDetail person={person} rates={rates} /> : null}
    </div>
  )
}

function PersonDetail({ person, rates }: { person: PersonRow; rates: PersonRateRow[] }) {
  const savePerson = useSavePerson()
  const [editingPerson, setEditingPerson] = useState(false)

  return (
    <div className="mb-3 ml-7 grid gap-5 rounded-card bg-fill-1 p-4 sm:grid-cols-2">
      <section>
        <p className="label-caps">Account</p>
        <AccountBlock person={person} />
      </section>

      <section>
        <div className="flex items-center justify-between gap-2">
          <p className="label-caps">Persoon</p>
          <button type="button" className="btn-ghost h-7 px-2 text-xs" onClick={() => setEditingPerson(true)}>
            <Pencil className="h-3.5 w-3.5" /> Bewerken
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <span id={`person-role-${person.id}`} className="text-sm text-ink-faint">Rol</span>
          <Select
            value={person.role}
            onValueChange={(value) => savePerson.mutate({ id: person.id, update: { role: value as UserRole } })}
            options={ROLE_SELECT_OPTIONS}
            ariaLabelledby={`person-role-${person.id}`}
            className="h-8"
          />
        </div>
        <dl className="mt-2 space-y-1 text-sm">
          {person.initials ? <div className="flex justify-between gap-3"><dt className="text-ink-faint">Initialen</dt><dd className="text-ink-soft">{person.initials}</dd></div> : null}
          {person.nameAliases.length > 0 ? <div className="flex justify-between gap-3"><dt className="text-ink-faint">Aliassen</dt><dd className="truncate text-ink-soft">{person.nameAliases.join(', ')}</dd></div> : null}
          {person.payoutDetails?.accountHolderName ? <div className="flex justify-between gap-3"><dt className="text-ink-faint">Naamgeving</dt><dd className="truncate text-ink-soft">{person.payoutDetails.accountHolderName}</dd></div> : null}
          {/* Een lege rekening blijft staan: zonder die regel is niet te zien of hij ontbreekt
              of dit paneel hem niet toont, en zonder rekening kan er niet uitbetaald worden. */}
          {person.payoutDetails ? <div className="flex justify-between gap-3"><dt className="text-ink-faint">IBAN</dt><dd className={cn('truncate', person.payoutDetails.iban ? 'font-mono text-ink-soft' : 'text-ink-faint')}>{person.payoutDetails.iban ?? 'Nog niet ingevuld'}</dd></div> : null}
        </dl>
        <label className="mt-3 flex items-center justify-between gap-3">
          <span className="text-sm text-ink-soft">Actief (login &amp; reminders)</span>
          <Toggle
            checked={person.active}
            onChange={(value) => savePerson.mutate({ id: person.id, update: { active: value } })}
            label={`Actief voor ${person.displayName}`}
          />
        </label>
        <label className="mt-3 flex items-center justify-between gap-3">
          <span className="text-sm text-ink-soft">Declarabiliteit zichtbaar</span>
          <Toggle
            checked={person.utilizationVisible}
            onChange={(value) => savePerson.mutate({ id: person.id, update: { utilizationVisible: value } })}
            label={`Declarabiliteit zichtbaar voor ${person.displayName}`}
          />
        </label>
        <label className="mt-3 flex items-center justify-between gap-3">
          <span className="text-sm text-ink-soft">In de vooruitblik</span>
          <Toggle
            checked={person.planningVisible}
            onChange={(value) => savePerson.mutate({ id: person.id, update: { planningVisible: value } })}
            label={`In de vooruitblik voor ${person.displayName}`}
          />
        </label>
        {person.planningVisible ? <PlanningTargetField person={person} /> : null}
        {person.role !== 'EXTERNAL' ? (
          <label className="mt-3 flex items-center justify-between gap-3">
            <span className="text-sm text-ink-soft">Wekelijkse uren verwacht</span>
            <Toggle
              checked={person.weeklyHoursRequired}
              onChange={(value) => savePerson.mutate({ id: person.id, update: { weeklyHoursRequired: value } })}
              label={`Wekelijkse uren verwacht voor ${person.displayName}`}
            />
          </label>
        ) : null}
        {person.role !== 'EXTERNAL' ? (
          <label className="mt-3 flex items-center justify-between gap-3">
            <span className="flex flex-col">
              <span className="text-sm text-ink-soft">Uitbetaling via eigen rekening</span>
              <span className="text-xs text-ink-faint">Regels gaan bij klantbetaling direct op betaald; geen inkoopfactuur of accordering.</span>
            </span>
            <Toggle
              checked={person.autoSettlePayout}
              onChange={(value) => savePerson.mutate({ id: person.id, update: { autoSettlePayout: value } })}
              label={`Uitbetaling via eigen rekening voor ${person.displayName}`}
            />
          </label>
        ) : null}
      </section>

      {person.role !== 'EXTERNAL' ? (
        <section>
          <p className="label-caps">Contracturen</p>
          <ContractHoursBlock person={person} />
        </section>
      ) : null}

      <section>
        <p className="label-caps">Uitbetaaltarief</p>
        <p className="mt-1 text-xs text-ink-faint">
          Wat wij deze persoon per uur betalen. Werkt alleen door als het verdeel-script van een klant ermee rekent; wat de klant zelf betaalt staat op de klantpagina.
        </p>
        <RatesBlock person={person} rates={rates} />
      </section>

      <section className="sm:col-span-2">
        <p className="label-caps">Toegang</p>
        <AccessBlock person={person} />
      </section>

      {editingPerson ? <PersonModal person={person} onClose={() => setEditingPerson(false)} /> : null}
    </div>
  )
}

/**
 * De ideale bezetting: hoeveel procent van zijn beschikbare uren deze persoon
 * idealiter vol zit. Leeg is de standaard van honderd procent: dan zijn de
 * contracturen vanzelf ook het streven en tekent de vooruitblik alleen de harde
 * grens. Het veld blijft leegbaar, want dat is de weg terug naar die standaard.
 *
 * De invoer gaat pas weg als je het veld verlaat of Enter geeft, want anders
 * schrijft "8" onderweg naar "80" een streven van acht procent weg.
 */
function PlanningTargetField({ person }: { person: PersonRow }) {
  const savePerson = useSavePerson()
  const stored = person.planningTargetPercent === null ? '' : String(person.planningTargetPercent)
  const [draft, setDraft] = useState(stored)
  const [invalid, setInvalid] = useState(false)

  function commit() {
    const value = draft.trim()
    if (value === stored) return setInvalid(false)
    if (value === '') {
      setInvalid(false)
      return savePerson.mutate({ id: person.id, update: { planningTargetPercent: null } })
    }
    const percent = Number(value)
    if (!/^\d+$/.test(value) || percent < 1 || percent > 100) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    savePerson.mutate(
      { id: person.id, update: { planningTargetPercent: percent } },
      { onError: () => setDraft(stored) },
    )
  }

  return (
    <div className="mt-3">
      <label className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-sm text-ink-soft">Ideale bezetting</span>
          <span className="text-xs text-ink-faint">Streven in de vooruitblik; leeg is de standaard van 100%. De contracturen blijven de grens.</span>
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <input
            value={draft}
            inputMode="numeric"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') {
                setDraft(stored)
                setInvalid(false)
              }
            }}
            aria-label={`Ideale bezetting van ${person.displayName} in procent`}
            aria-invalid={invalid}
            placeholder="100"
            className={cn('field h-8 w-16 text-right text-sm', invalid && 'ring-1 ring-status-overdue')}
          />
          <span className="text-sm text-ink-faint">%</span>
        </span>
      </label>
      {invalid ? <p className="mt-1 text-right text-xs text-status-overdue">Een heel getal van 1 tot en met 100.</p> : null}
    </div>
  )
}

function AccountBlock({ person }: { person: PersonRow }) {
  const account = person.account
  const [inviting, setInviting] = useState(false)
  const [changingEmail, setChangingEmail] = useState(false)

  if (!account?.exists) {
    return (
      <div className="mt-2">
        <p className="text-sm text-ink-faint">Deze persoon kan nog niet inloggen.</p>
        <button type="button" className="btn-secondary mt-2 h-8 px-2.5 text-sm" onClick={() => setInviting(true)}>
          <KeyRound className="h-4 w-4" /> Account registreren
        </button>
        {inviting ? <AccountModal person={person} onClose={() => setInviting(false)} /> : null}
      </div>
    )
  }

  return (
    <div className="mt-2 space-y-2.5 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="text-ink-faint">E-mail</span>
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate text-ink-soft">{account.email}</span>
          <button
            type="button"
            className="btn-ghost h-6 shrink-0 px-1.5"
            onClick={() => setChangingEmail(true)}
            aria-label={`E-mailadres van ${person.displayName} wijzigen`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>
      <div className="flex justify-between gap-3">
        <span className="text-ink-faint">Rol</span>
        <span className="text-ink-soft">{ROLE_LABEL[person.role]}</span>
      </div>
      <div className="flex justify-between gap-3">
        <span className="text-ink-faint">Entra-koppeling</span>
        <span className="text-ink-soft">{account.entraLinked ? 'gekoppeld' : 'wacht op eerste login'}</span>
      </div>
      <PersonOAuthClientsBlock person={person} count={account.activeOAuthClientCount ?? 0} />
      {changingEmail ? (
        <AccountEmailModal person={person} current={account.email ?? ''} onClose={() => setChangingEmail(false)} />
      ) : null}
    </div>
  )
}

function PersonOAuthClientsBlock({ person, count }: { person: PersonRow; count: number }) {
  const [expanded, setExpanded] = useState(false)
  const clientsQuery = usePersonOAuthClients(person.id, expanded)
  const revoke = useRevokePersonOAuthClient()
  const reactivate = useReactivatePersonOAuthClient()
  const clients = clientsQuery.data ?? []

  return (
    <div>
      <button type="button" onClick={() => setExpanded((v) => !v)} className="flex w-full items-center justify-between gap-3 text-left">
        <span className="text-ink-faint">Actieve koppelingen</span>
        <span className="flex items-center gap-1.5 text-ink-soft">
          <span className="num">{count}</span>
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expanded ? 'rotate-90' : '')} strokeWidth={1.85} />
        </span>
      </button>
      {expanded ? (
        clientsQuery.isLoading ? (
          <Skeleton className="mt-2 h-12 w-full" />
        ) : clients.length === 0 ? (
          <p className="mt-2 text-xs text-ink-faint">Nog geen koppelingen.</p>
        ) : (
          <div className="mt-2 space-y-1.5">
            {clients.map((client) => (
              <div key={client.id} className="flex items-center justify-between gap-2 rounded-md bg-fill-2 px-2.5 py-1.5">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-ink">{client.name}</p>
                  <p className="truncate text-[11px] text-ink-faint">
                    {client.lastUsedAt ? `laatst actief ${formatDate(client.lastUsedAt)}` : 'nog niet gebruikt'}
                    {' · '}
                    {client.active ? 'actief' : 'uitgeschakeld'}
                  </p>
                </div>
                {client.active ? (
                  <button
                    type="button"
                    className="btn-ghost h-6 shrink-0 px-2 text-[11px] font-medium text-status-overdue"
                    onClick={() => revoke.mutate({ personId: person.id, clientId: client.id })}
                  >
                    Verwijderen
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-ghost h-6 shrink-0 px-2 text-[11px] font-medium"
                    onClick={() => reactivate.mutate({ personId: person.id, clientId: client.id })}
                  >
                    Inschakelen
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      ) : null}
    </div>
  )
}

function AccessBlock({ person }: { person: PersonRow }) {
  const access = person.access
  const updateAccess = useUpdateAccess()
  const clientList = useClientList(undefined, Boolean(access))

  if (!access) {
    return <p className="mt-2 text-sm text-ink-faint">Alleen beheerders kunnen toegang beheren.</p>
  }

  const clientIds = access.clients.map((c) => c.clientId)
  const grantedClientIds = new Set(clientIds)
  const addableClients = (clientList.data ?? []).filter((c) => !grantedClientIds.has(c.id))
  const unrestricted = person.role === 'ADMIN' || person.role === 'PARTNER'

  function setClients(nextClientIds: string[]) {
    updateAccess.mutate({ personId: person.id, input: { clientIds: nextClientIds } })
  }

  return (
    <div className="mt-2 space-y-3">
      <p className="text-xs text-ink-faint">
        {unrestricted
          ? `${ROLE_LABEL[person.role]} ziet altijd alle klanten. Een koppeling is hier alleen een aantekening: die maakt zichtbaar op de klantkaart dat deze persoon voor die klant werkt.`
          : 'Deze persoon ziet uitsluitend de klanten die hier staan, en mag alleen daar uren op boeken. Een koppeling dekt alle projecten van de klant, ook nieuwe. Zonder koppeling ziet deze persoon niets.'}
      </p>

      <div className="flex flex-wrap items-center gap-1.5">
        {access.clients.map((c) => (
          <Chip
            key={c.clientId}
            label={c.clientName}
            onRemove={() => setClients(clientIds.filter((id) => id !== c.clientId))}
            disabled={updateAccess.isPending}
          />
        ))}
        {access.clients.length === 0 ? <span className="text-sm text-ink-faint">Nog geen klant gekoppeld.</span> : null}
      </div>
      {addableClients.length > 0 ? (
        <div className="max-w-xs">
          <Select
            value="none"
            onValueChange={(id) => id !== 'none' && setClients([...clientIds, id])}
            disabled={updateAccess.isPending}
            placeholder="Klant koppelen…"
            options={[{ value: 'none', label: 'Klant koppelen…' }, ...addableClients.map((c) => ({ value: c.id, label: c.companyName }))]}
          />
        </div>
      ) : null}
    </div>
  )
}

function Chip({ label, onRemove, disabled }: { label: string; onRemove: () => void; disabled?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-pill bg-accent-600 px-2.5 py-1 text-xs text-white">
      {label}
      <button type="button" onClick={onRemove} disabled={disabled} aria-label={`${label} verwijderen`} className="text-white/80 hover:text-white">
        <Trash2 className="h-3 w-3" />
      </button>
    </span>
  )
}

function ContractHoursBlock({ person }: { person: PersonRow }) {
  const del = useDeleteContractHours()
  const [adding, setAdding] = useState(false)
  const current = person.contractHours?.current ?? null
  const history = person.contractHours?.history ?? []

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-ink-soft">
          {current ? `${formatContractHours(toNumber(current.hoursPerWeek))} u/week` : 'Flexibel (geen contracturen)'}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {current ? (
            <button
              type="button"
              className="btn-ghost h-7 w-7 p-0 text-status-overdue"
              onClick={() => del.mutate({ personId: person.id, contractId: current.id })}
              aria-label={`Laatste contracturen-periode van ${person.displayName} verwijderen`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <button type="button" className="btn-ghost h-7 w-7 p-0" onClick={() => setAdding(true)} aria-label={`Contracturen-periode toevoegen voor ${person.displayName}`}>
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {current ? (
        <p className="text-xs text-ink-faint">
          {current.validFrom} t/m {current.validTo ?? 'actueel'}
          {history.length > 1 ? ` · ${history.length} periodes` : ''}
        </p>
      ) : null}
      {adding ? <ContractHoursModal personId={person.id} personName={person.displayName} onClose={() => setAdding(false)} /> : null}
    </div>
  )
}

function RatesBlock({ person, rates }: { person: PersonRow; rates: PersonRateRow[] }) {
  const [editing, setEditing] = useState<PersonRateRow | 'new' | null>(null)

  return (
    <div className="mt-2 space-y-2">
      {rates.length === 0 ? (
        <p className="text-sm text-ink-faint">Nog geen uitbetaaltarief.</p>
      ) : (
        <div className="space-y-1.5">
          {rates.map((rate) => (
            <div key={rate.id} className="flex items-center justify-between gap-3">
              <span className="text-xs text-ink-faint">{rate.validFrom} t/m {rate.validTo ?? 'actueel'}</span>
              <div className="flex items-center gap-1.5">
                <span className="money text-sm font-medium text-ink">{formatEuros(toNumber(rate.hourlyRate))}/u</span>
                <button type="button" className="btn-ghost h-7 w-7 p-0" onClick={() => setEditing(rate)} aria-label="Uitbetaaltarief bewerken">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button type="button" className="btn-ghost h-7 px-2 text-xs" onClick={() => setEditing('new')}>
        <Plus className="h-3.5 w-3.5" /> Nieuw uitbetaaltarief
      </button>
      {editing ? (
        <RateModal
          rate={editing === 'new' ? null : editing}
          lockedPerson={person}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}
